import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { buildFilterQuery } from '@sorare/core/src/components/search/InstantSearch/utils';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { joinFiltersWithAnd } from '@sorare/core/src/lib/algolia';

import { useMarketHomeFilters } from 'hooks/useMarketHomeFilters';

import { SlideShow } from '../SlideShow';
import { Performer } from './Performer';
import { HeaderSection } from '../HeaderSection';
import type {
  MarketHomePageBestPerformersQuery,
  MarketHomePageBestPerformersQueryVariables,
} from './__generated__/index.graphql';

const SKELETONS = Array.from({ length: 16 }, (_, index) => ({ index }));

const PerformerSkeleton = styled(Skeleton)`
  width: 320px;
  border-radius: var(--double-unit);
  height: 66px;
`;

const MARKET_HOME_PAGE_BEST_PERFORMERS_QUERY = gql`
  query MarketHomePageBestPerformersQuery(
    $filters: String!
    $query: String!
    $page: Int!
    $pageSize: Int!
  ) {
    searchPlayers(
      query: $query
      page: $page
      pageSize: $pageSize
      advancedFilters: $filters
      refinements: [
        {
          field: "so5.last_five_so5_appearances"
          operator: GREATER_THAN_OR_EQUAL
          values: { integerValue: 5 }
        }
      ]
      sorts: [{ direction: DESC, field: "so5.last_five_so5_average_score" }]
    ) {
      hits {
        id
        anyPlayer {
          slug
          displayName
          ...Performer_anyPlayer
        }
      }
    }
  }
  ${Performer.fragments.anyPlayer}
` as TypedDocumentNode<
  MarketHomePageBestPerformersQuery,
  MarketHomePageBestPerformersQueryVariables
>;

type Props = {
  onStatsClick: (slug: string) => void;
};

export const BestPerformers = ({ onStatsClick }: Props) => {
  const { algoliaFilters, scoutAllUrl } = useMarketHomeFilters();
  const filters = joinFiltersWithAnd([algoliaFilters]);

  const { data, loading } = useQuery(MARKET_HOME_PAGE_BEST_PERFORMERS_QUERY, {
    variables: {
      filters,
      query: '',
      page: 1,
      pageSize: 30,
    },
  });

  const anyPlayers = (
    data?.searchPlayers.hits?.map(hit => hit.anyPlayer) || []
  ).filter(Boolean);

  if (anyPlayers.length === 0) {
    return null;
  }

  return (
    <Vertical gap={2}>
      <HeaderSection
        title={
          <FormattedMessage
            id="MarketHomePage.BestPerformers.title"
            defaultMessage="Best Performers"
          />
        }
        button={{
          label: (
            <FormattedMessage
              id="MarketHomePage.BestPerformers.scoutAll"
              defaultMessage="Scout All"
            />
          ),
          to: `${scoutAllUrl}?${buildFilterQuery({
            [SEARCH_PARAMS.SORT]: 'DESC-L5',
            [SEARCH_PARAMS.SO5_5_APPEARANCES]: '5:5',
          })}`,
        }}
      />
      <SlideShow>
        <Horizontal>
          {!!loading && SKELETONS.map(s => <PerformerSkeleton key={s.index} />)}
          {!loading &&
            anyPlayers?.map(player => (
              <Performer
                key={player?.slug}
                anyPlayer={player}
                onStatsClick={onStatsClick}
              />
            ))}
        </Horizontal>
      </SlideShow>
    </Vertical>
  );
};
